/**
 * In-memory holder for the demo invite code. Non-admin users need this
 * before any chat request is accepted by the backend (the `X-Demo-Code`
 * header is matched against `adminConfig.inviteCode`).
 *
 * Persistence model mirrors the auth-token policy: memory-only, never
 * written to localStorage / sessionStorage / cookies. On a page reload
 * the code is gone; the user re-enters it in Settings, or visits a URL
 * with `?code=…` again (admin distributes invite URLs).
 *
 * Boot behavior:
 *   - If the URL has `?invite=…`, capture it, strip it from history (so the
 *     code doesn't sit visibly in the address bar or get bookmarked),
 *     and seed the in-memory state.
 *   - Otherwise initial state is null and the SettingsPanel surfaces an input.
 *
 * IMPORTANT: the param name is `invite`, NOT `code`. `code` is reserved by
 * the Cognito OAuth callback (`/auth/callback?code=…&state=…`) — if we
 * intercepted that we'd silently break the sign-in flow.
 */
import { createContext, useContext, useEffect, useState, ReactNode } from "react";

const URL_PARAM = "invite";

interface DemoCodeState {
  /** The current demo code, or null when none has been set this session. */
  code: string | null;
  setCode: (code: string | null) => void;
}

const Ctx = createContext<DemoCodeState | null>(null);

export function DemoCodeProvider({ children }: { children: ReactNode }) {
  const [code, setCode] = useState<string | null>(() => {
    if (typeof window === "undefined") return null;
    const params = new URLSearchParams(window.location.search);
    const fromUrl = params.get(URL_PARAM);
    return fromUrl && fromUrl.trim().length > 0 ? fromUrl.trim() : null;
  });

  // Strip ?invite= from the visible URL after capture so it doesn't linger
  // in the address bar / browser history. We do this in an effect (not in
  // useState init) so React's strict-mode double invocation doesn't run
  // history.replaceState() during render.
  useEffect(() => {
    if (typeof window === "undefined") return;
    const params = new URLSearchParams(window.location.search);
    if (params.has(URL_PARAM)) {
      params.delete(URL_PARAM);
      const rest = params.toString();
      const newUrl = window.location.pathname + (rest ? `?${rest}` : "") + window.location.hash;
      window.history.replaceState({}, "", newUrl);
    }
  }, []);

  return <Ctx.Provider value={{ code, setCode }}>{children}</Ctx.Provider>;
}

export function useDemoCode(): DemoCodeState {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error("useDemoCode outside DemoCodeProvider");
  return ctx;
}
